import { createApi } from '@reduxjs/toolkit/query/react';
import { saveAs } from 'file-saver';
import { createBaseQuery, toQueryParams } from 'helpers/api';

import { intercomConversationsApi } from 'api/intercom-conversations-api';

export const candidateChecksApi = createApi({
  reducerPath: 'candidateChecksApi',
  baseQuery: createBaseQuery('candidate_checks', { timeout: 200000 }),
  endpoints: builder => ({
    getChecks: builder.query({
      query: ({ type, params }) => `/types/${type}?${toQueryParams(params)}`,
    }),
    getAllCandidateChecks: builder.query({
      query: params => `/admin?${toQueryParams(params)}`,
    }),
    getAdminKeyDetails: builder.query({
      query: () => `/admin/key_details`,
      transformResponse: ({ result }) => result,
    }),
    getAdminSidebarCounts: builder.query({
      query: params => `/admin/sidebar_counts?${toQueryParams(params)}`,
      transformResponse: ({ result }) => result,
    }),
    bulkUpdateSupportUsers: builder.mutation({
      query: data => ({
        url: `/bulk/update_support_users`,
        method: 'POST',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    bulkAssignmentSummary: builder.mutation({
      query: data => ({
        url: `/bulk/assignment_summary`,
        method: 'POST',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    downloadAdminCandidateChecksReport: builder.query({
      queryFn: async ({ queryParams }, api, extraOptions, baseQuery) => {
        const { data: file } = await baseQuery({
          url: `/admin/download?${toQueryParams(queryParams)}`,
          responseHandler: async response => {
            const blob = await response.blob();
            return blob;
          },
          cache: 'no-cache',
        });

        saveAs(file, 'candidate_checks_export.csv'); // filename here
        return { data: null };
      },
    }),
    updateCandidateCheck: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    updateCandidateCheckAdmin: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/admin`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    createCandidateCheck: builder.mutation({
      query: ({ candidateId, data }) => {
        return {
          url: `/candidate/${candidateId}/`,
          method: 'POST',
          body: data,
        };
      },
      transformResponse: ({ result }) => result,
    }),
    cancelCandidateCheck: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/cancel`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    cancelCandidateCheckAdmin: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/admin/cancel`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    deferCandidateCheck: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/defer`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    updateStatus: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/update_status`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    updateCheckSupportUser: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/update_support_user`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    createCheckHistory: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/admin/history`,
        method: 'POST',
        body: data,
      }),
      transformResponse: ({ result }) => result,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(intercomConversationsApi.util.invalidateTags(['IntercomConversations']));
        } catch (error) {
          return error;
        }
      },
    }),
    getInternalCheckHistory: builder.query({
      query: ({ id }) => `/${id}/admin/history`,
    }),
    updateCheckHistory: builder.mutation({
      query: ({ id, check_history_id, data }) => ({
        url: `/${id}/admin/history/${check_history_id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(intercomConversationsApi.util.invalidateTags(['IntercomConversations']));
        } catch (error) {
          return error;
        }
      },
    }),
    deleteCheckHistory: builder.mutation({
      query: ({ id, check_history_id }) => ({
        url: `/${id}/admin/history/${check_history_id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(intercomConversationsApi.util.invalidateTags(['IntercomConversations']));
        } catch (error) {
          return error;
        }
      },
    }),
    restoreCandidateCheck: builder.mutation({
      query: id => ({
        url: `/${id}/restore`,
        method: 'PUT',
      }),
      transformResponse: ({ result }) => result,
    }),
    generateReport: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/report/regenerate`,
        method: 'POST',
        body: data,
      }),
    }),
    generateSubchecksCombinedReport: builder.mutation({
      query: id => ({
        url: `/${id}/sub_checks/combined_report`,
        method: 'POST',
      }),
    }),
    regenerateSubChecks: builder.mutation({
      query: id => ({
        url: `/${id}/sub_checks/regenerate`,
        method: 'POST',
      }),
      transformResponse: ({ result }) => result,
    }),
    sendPartnerWebhook: builder.mutation({
      query: id => ({
        url: `/${id}/webhooks`,
        method: 'POST',
      }),
    }),
    getProviderForm: builder.query({
      queryFn: async (
        { check, candidate, wetSignature = false, subCheckId },
        api,
        extraOptions,
        baseQuery,
      ) => {
        const queryParams = {};
        if (wetSignature) queryParams.wet_signature = '1';
        if (subCheckId != null) queryParams.sub_check_id = subCheckId;
        const query = toQueryParams(queryParams);
        const params = query ? `?${query}` : '';
        const { data: file } = await baseQuery({
          url: `/${check.id}/provider/form${params}`,
          method: 'GET',
          responseHandler: async response => {
            const blob = await response.blob();
            return blob;
          },
          cache: 'no-cache',
        });
        const suffix = wetSignature ? '_wet_signature_form' : '_form';
        const subCheckSuffix = subCheckId != null ? `_subcheck_${subCheckId}` : '';
        saveAs(file, `${candidate.name}_${check.type}${subCheckSuffix}${suffix}`);
        return { data: null };
      },
    }),
    getCandidateCheckResult: builder.query({
      queryFn: async ({ check, candidate, isFile = true }, api, extraOptions, baseQuery) => {
        const response = await baseQuery({
          url: `/${check.id}/result`,
          method: 'GET',
          responseHandler: async response => {
            if (isFile) {
              const blob = await response.blob();
              return blob;
            } else {
              const jsonResponse = await response.json();
              return jsonResponse.result;
            }
          },
          cache: 'no-cache',
        });

        if (response.error) {
          return {
            error: { status: response.error.status },
          };
        }

        if (isFile) saveAs(response.data, `${candidate.name}_${check.type}`);
        else window.open(response.data, '_blank');

        return { data: null };
      },
    }),
    getCustomCandidateCheckResult: builder.query({
      queryFn: async ({ check, candidate }, api, extraOptions, baseQuery) => {
        const response = await baseQuery({
          url: `/${check.id}/custom_result`,
          method: 'GET',
          responseHandler: async response => {
            const blob = await response.blob();
            return blob;
          },
          cache: 'no-cache',
        });

        if (response.error) {
          return {
            error: { status: response.error.status },
          };
        }

        saveAs(response.data, `${candidate.name}_${check.type}-report.pdf`);
        return { data: null };
      },
    }),
    candidateCheckLogViewResult: builder.mutation({
      query: id => ({
        url: `/${id}/result/log`,
        method: 'POST',
      }),
    }),
    getChecskWidgetCount: builder.query({
      query: () => `/widget/counts`,
    }),
    sendToProvider: builder.mutation({
      query: arg => {
        const id = typeof arg === 'object' ? arg.id : arg;
        const subCheckId = typeof arg === 'object' ? arg.subCheckId : undefined;
        return {
          url: `/${id}/provider`,
          method: 'POST',
          body: subCheckId != null ? { sub_check_id: subCheckId } : undefined,
        };
      },
      transformResponse: ({ result }) => result,
    }),
    renewCandidateCheck: builder.mutation({
      query: id => ({
        url: `/${id}/renew`,
        method: 'POST',
      }),
      transformResponse: ({ result }) => result,
    }),
    getLastCheckOfTypeForCandidateByToken: builder.query({
      query: ({ token, type }) => `/token/${token}/types/${type}`,
      transformResponse: ({ result }) => result,
    }),
    getAnswers: builder.query({
      query: ({ id }) => `/${id}/answers`,
    }),
    getResolvedCheckType: builder.query({
      query: ({ id, subCheckId }) => {
        const params = subCheckId ? { sub_check_id: subCheckId } : {};
        return `/${id}/check_type?${toQueryParams(params)}`;
      },
      transformResponse: ({ result }) => result,
    }),
  }),
});

export const {
  useLazyGetLastCheckOfTypeForCandidateByTokenQuery,
  useLazyGetAllCandidateChecksQuery,
  useLazyDownloadAdminCandidateChecksReportQuery,
  useLazyGetCandidateCheckResultQuery,
  useLazyGetCustomCandidateCheckResultQuery,
  useCreateCandidateCheckMutation,
  useCancelCandidateCheckMutation,
  useCancelCandidateCheckAdminMutation,
  useDeferCandidateCheckMutation,
  useRestoreCandidateCheckMutation,
  useUpdateCandidateCheckMutation,
  useUpdateCandidateCheckAdminMutation,
  useSendToProviderMutation,
  useCandidateCheckLogViewResultMutation,
  useLazyGetChecksQuery,
  useGetChecskWidgetCountQuery,
  useLazyGetProviderFormQuery,
  useGenerateReportMutation,
  useGenerateSubchecksCombinedReportMutation,
  useRegenerateSubChecksMutation,
  useGetAdminKeyDetailsQuery,
  useGetAdminSidebarCountsQuery,
  useBulkUpdateSupportUsersMutation,
  useBulkAssignmentSummaryMutation,
  useGetAnswersQuery,
  useRenewCandidateCheckMutation,
  useSendPartnerWebhookMutation,
  useUpdateStatusMutation,
  useUpdateCheckSupportUserMutation,
  useCreateCheckHistoryMutation,
  useLazyGetInternalCheckHistoryQuery,
  useUpdateCheckHistoryMutation,
  useDeleteCheckHistoryMutation,
  useLazyGetResolvedCheckTypeQuery,
} = candidateChecksApi;
